import { Controller } from '@hotwired/stimulus';

/**
 * A generic state management controller
 *
 * See the Storybook examples for usage! `npm run storybook`
 *
 * Usage:
 * div data-controller="state" data-state-initial-value='{"key1": "value1", "key2": "value2", "show": true}'
 *   / Updates the state based on the input value
 *   input data-state-target="input" data-key="key1" type="text" data-action="change->state#updateState"
 *   input data-state-target="input" data-key="key1" type="radio" value="value1"
 *   = form.input :key1, as: :radio, input_html: { data: { state_target: 'input', key: 'key1' } }
 *
 *   / Sets textContent to the state value
 *   span data-state-target="text" data-key="key1"
 *
 *   / Shows/hides the element based on the state using `hideClass` value (default: d-none)
 *   span data-state-target="show" data-key="show"
 *     | Show me
 *   / data-toggle-key will toggle a boolean state value between true and false
 *   button data-toggle-key="show"
 *
 *   / Arrays of target values is also supported with or without brackets
 *   span data-state-target="show" data-key="show" data-target-value='[1, 2]'
 *     | Show me
 *   span data-state-target="show" data-key="show" data-target-value='"value1", "value2"'
 *     | Show me
 *
 *   / Adds/removes the template contents from the DOM
 *   template data-state-target="if" data-key="show"
 *     span
 *       | Show me
 *
 * NOTE: If you are using nested `if` targets, it is advisable to use a separate controller for each nested state.
 *       This simplifies the potential for the cloned template content to not initialize the nested state controller correctly.
 *
 * div data-controller="state" data-state-initial-value='{"show1": true}'
 *   template data-state-target="if" data-key="show1"
 *     div data-controller="state" data-state-initial-value='{"show2": true}'
 *       template data-state-target="if" data-key="show2"
 *         span
 *           | Show me
 *
 * You can get away without the extra state controller but you must set up the `data-action` yourself:
 *
 * div data-controller="state" data-state-initial-value='{"show1": true}'
 *   template data-state-target="if" data-key="show1"
 *     input data-state-target="input" data-key="show2" type="checkbox" data-action="change->state#updateState"
 *     template data-state-target="if" data-key="show2"
 *       span
 *         | Show me
 */

export default class extends Controller {
  static targets = ['input', 'text', 'show', 'if']

  static values = {
    initial: Object,
    hideClass: { type: String, default: 'd-none' }
  }

  initialize() {
    this.state = {};
  }

  connect() {
    // Initialize state with initial values if provided
    if (this.hasInitialValue) {
      this.state = { ...this.initialValue };
      this.updateUI();
    }

    this.setupInputListeners();
  }

  setupInputListeners() {
    this.inputTargets.forEach((input) => {
      input.addEventListener('change', this.updateState.bind(this));
    });
  }

  updateState(event) {
    const target = event.target;
    const toggleKey = target.dataset.toggleKey;
    const key = target.dataset.key || toggleKey;
    let value;

    switch (target.type) {
      case 'checkbox':
        value = target.checked;
        break;
      default:
        if (toggleKey) {
          value = !this.state[key];
        } else {
          value = target.dataset.value || target.value;
        }
    }

    this.state[key] = value;
    this.updateUI();
  }

  updateUI() {
    this.updateInputTargets();
    this.updateTextTargets();
    this.updateShowTargets();
    this.updateIfTargets();
  }

  updateInputTargets() {
    this.inputTargets.forEach((input) => {
      const key = input.dataset.key;

      if (!key || !(key in this.state)) return;

      switch (input.type) {
        case 'radio':
          input.checked = input.value === this.state[key];
          break;
        case 'checkbox':
          input.checked = this.state[key];
          break;
        default:
          input.value = this.state[key];
      }
    });
  }

  updateTextTargets() {
    this.textTargets.forEach((text) => {
      const key = text.dataset.key;
      if (!key || !(key in this.state)) return;

      text.textContent = this.state[key];
    });
  }

  stateIsTruthy(key, targetValue) {
    let stateValue;
    try {
      // Try to parse the state value as JSON
      stateValue = this.state[key] && JSON.parse(this.state[key]);
    } catch {
      // If it's not a valid JSON, use the state value as is
      stateValue = this.state[key];
    }
    if (targetValue === undefined) return !!stateValue;

    let targetValueArray;
    if (targetValue.startsWith('[') && targetValue.endsWith(']')) {
      // Handle array format '[item1, item2]'
      targetValueArray = JSON.parse(targetValue);
    } else {
      // Handle comma-separated format 'item1, item2'
      targetValueArray = targetValue.split(',').map((item) => item.trim());
    }

    return targetValueArray.includes(stateValue);
  }

  updateShowTargets() {
    this.showTargets.forEach((show) => {
      const key = show.dataset.key;
      if (!key || !(key in this.state)) return;

      const shouldShow = this.stateIsTruthy(key, show.dataset.targetValue);

      show.classList.toggle(this.hideClassValue, !shouldShow);
    });
  }

  updateIfTargets() {
    this.ifTargets.forEach((template) => {
      if (!(template instanceof HTMLTemplateElement)) {
        // eslint-disable-next-line no-console
        console.warn('data-state-target="if" should be used on <template> elements. Example:\n<template data-state-target="if" data-key="show">...</template>');
        return;
      }

      const key = template.dataset.key;
      if (!key || !(key in this.state)) return;

      const shouldShow = this.stateIsTruthy(key, template.dataset.targetValue);
      shouldShow ? this.showIfTemplate(template) : this.hideIfTemplate(template);
    });
  }

  showIfTemplate(template) {
    if (template._clone) return template._clone;

    const elements = Array.from(template.content.childNodes).map((child) => child.cloneNode(true));

    if (elements.length === 0) {
      // eslint-disable-next-line no-console
      console.warn('Template must contain an element node', template);
      return null;
    }

    template._clone = document.createElement('div');
    elements.forEach((element) => {
      template._clone.appendChild(element);
    });
    template.after(template._clone);
    document.dispatchEvent(new CustomEvent('state:template-rendered', { detail: { container: template._clone } }));

    template._undoIf = () => {
      template._clone.remove();
      delete template._clone;
    };

    // Update the UI in case we have newly nested state elements
    this.updateUI();

    return elements;
  }

  hideIfTemplate(template) {
    if (!template._undoIf) return;

    template._undoIf();
    delete template._undoIf;
  }

  disconnect() {
    this.inputTargets.forEach((input) => {
      input.removeEventListener('change', this.updateState.bind(this));
    });

    this.ifTargets.forEach((template) => {
      if (template._undoIf) template._undoIf();
    });
  }
}
